import React from 'react';
import { I18n } from '@aws-amplify/core';
import { Typography, Spin, message } from 'antd';

import { usePsychosocialAvaliationContext } from './context';
import { useResponsive } from './SurveyResponse/hooks/useResponsive';
import { useGetParticipationsByEmployee } from '@/views/PsychosocialReport/hooks/useGetParticipationsByEmployee';
import type { ParticipationItem } from '@/types/Psychosocial';
import {
	CompletedSurveySectionTitle,
	DashboardCardCompleted,
	SurveySectionTitle,
	LoadingContainer,
	CardTitleMobile,
	DashboardCard,
	DashboardRow,
	CardTitle,
	CardText
} from './styles';

const { Text } = Typography;

export function SurveysDashboard() {
	const { isMobile } = useResponsive();
	const { surveyData } = usePsychosocialAvaliationContext();

	const employeeId = surveyData?.participation?.employee_id || '';

	const { isLoading, data } = useGetParticipationsByEmployee({
		employee_id: employeeId
	});

	const openSurveys = data?.open || [];
	const completedSurveys = data?.completed || [];

	function handleOnClick(survey: ParticipationItem): void {
		if (survey.survey_url) {
			window.location.href = survey.survey_url;
		} else {
			message.error(`Survey ${survey.survey_name} does not have an available URL`);
		}
	}

	if (isLoading) {
		return (
			<LoadingContainer>
				<Spin size="large" />
			</LoadingContainer>
		);
	}

	if (isMobile) {
		return (
			<>
				<SurveySectionTitle level={4}>{I18n.get('Open Surveys')}</SurveySectionTitle>
				{openSurveys.length === 0 && (
					<Text type="secondary">{I18n.get('There are no open surveys at the moment.')}</Text>
				)}
				{openSurveys.map((survey) => (
					<DashboardCard key={survey.id} hoverable onClick={() => handleOnClick(survey)}>
						<CardTitleMobile level={5}>{survey.survey_name}</CardTitleMobile>
						<CardText type="secondary">
							{I18n.get('Due date')}:
							<br />
							{survey.expires_at
								? new Date(survey.expires_at).toLocaleDateString(window.navigator.language)
								: I18n.get('No due date defined')}
						</CardText>
					</DashboardCard>
				))}
				<CompletedSurveySectionTitle level={4}>{I18n.get('Completed Surveys')}</CompletedSurveySectionTitle>
				{completedSurveys.length === 0 && (
					<Text type="secondary">{I18n.get('There are no completed surveys.')}</Text>
				)}
				{completedSurveys.map((survey) => (
					<DashboardCardCompleted key={survey.id}>
						<CardTitleMobile level={5}>{survey.survey_name}</CardTitleMobile>
						<CardText type="secondary" style={{ color: 'rgba(80, 80, 80, 1)' }}>
							{I18n.get('Completed on')}:
							<br />
							{new Date(survey.created_at).toLocaleDateString(window.navigator.language)}
						</CardText>
					</DashboardCardCompleted>
				))}
			</>
		);
	}

	return (
		<>
			<SurveySectionTitle level={3}>{I18n.get('Open Surveys')}</SurveySectionTitle>
			{openSurveys.length === 0 && (
				<Text type="secondary">{I18n.get('There are no open surveys at the moment.')}</Text>
			)}
			<DashboardRow justify="start" gutter={[24, 24]}>
				{openSurveys.map((survey) => (
					<DashboardCard key={survey.id} hoverable onClick={() => handleOnClick(survey)}>
						<CardTitle level={5}>{survey.survey_name}</CardTitle>
						<Text type="secondary">
							{I18n.get('Due date')}:
							<br />
							{survey.expires_at
								? new Date(survey.expires_at).toLocaleDateString(window.navigator.language)
								: I18n.get('No due date defined')}
						</Text>
					</DashboardCard>
				))}
			</DashboardRow>
			<CompletedSurveySectionTitle level={3}>{I18n.get('Surveys already completed')}</CompletedSurveySectionTitle>
			{completedSurveys.length === 0 && (
				<Text type="secondary">{I18n.get('There are no completed surveys.')}</Text>
			)}
			<DashboardRow justify="start" gutter={[24, 24]}>
				{completedSurveys.map((survey) => (
					<DashboardCardCompleted key={survey.id}>
						<CardTitle level={5}>{survey.survey_name}</CardTitle>
						<Text type="secondary" style={{ color: 'rgba(80, 80, 80, 1)' }}>
							{I18n.get('Completed on')}:
							<br />
							{new Date(survey.created_at).toLocaleDateString(window.navigator.language)}
						</Text>
					</DashboardCardCompleted>
				))}
			</DashboardRow>
		</>
	);
}
